import { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import imageCompression from "browser-image-compression";
import JSZip from "jszip";
import {
  PackageCheck,
  Sparkles,
  UploadCloud,
  ImageIcon,
  X,
  Download,
  FileArchive,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Slider } from "../../ui/slider";
import { toast } from "sonner";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];
const MAX_FILES = 10;

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const makeId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const isAcceptedFile = (file) =>
  ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

export const PriceReducerPage = () => {
  const [quality, setQuality] = useState(70);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const inputRef = useRef(null);

  const doneFiles = files.filter((f) => f.status === "done");
  const totalOriginal = doneFiles.reduce((s, f) => s + f.originalSize, 0);
  const totalCompressed = doneFiles.reduce((s, f) => s + (f.compressedSize || 0), 0);
  const avgSavedPct = totalOriginal ? Math.round((1 - totalCompressed / totalOriginal) * 100) : 0;

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    const accepted = incoming.filter(isAcceptedFile);
    const rejected = incoming.filter((f) => !isAcceptedFile(f));
    if (rejected.length) {
      toast.error(`Unsupported file${rejected.length > 1 ? "s" : ""} skipped`, {
        description: `${rejected.map((f) => f.name).join(", ")} — only JPG, PNG and WEBP are supported.`,
      });
    }

    setFiles((prev) => {
      const room = MAX_FILES - prev.length;
      if (room <= 0) {
        toast.error(`You can compress up to ${MAX_FILES} images at a time`);
        return prev;
      }
      const toAdd = accepted.slice(0, room);
      if (accepted.length > toAdd.length) {
        toast.error(`Only added ${toAdd.length} of ${accepted.length} images`, {
          description: `Max ${MAX_FILES} images at a time.`,
        });
      }
      const entries = toAdd.map((file) => ({
        id: makeId(),
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
        originalSize: file.size,
        status: "pending",
        compressedBlob: null,
        compressedSize: null,
        compressedUrl: null,
        error: null,
      }));
      return [...prev, ...entries];
    });
  }, []);

  const removeFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        if (target.compressedUrl) URL.revokeObjectURL(target.compressedUrl);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const clearAll = () => {
    files.forEach((f) => {
      URL.revokeObjectURL(f.previewUrl);
      if (f.compressedUrl) URL.revokeObjectURL(f.compressedUrl);
    });
    setFiles([]);
  };

  const compressOne = async (entry) => {
    try {
      const compressed = await imageCompression(entry.file, {
        initialQuality: Math.min(0.95, Math.max(0.05, quality / 100)),
        useWebWorker: true,
        alwaysKeepResolution: true,
      });
      return {
        ...entry,
        status: "done",
        compressedBlob: compressed,
        compressedSize: compressed.size,
        compressedUrl: URL.createObjectURL(compressed),
        error: null,
      };
    } catch (err) {
      return { ...entry, status: "error", error: err?.message || "Compression failed" };
    }
  };

  const compressAll = async () => {
    if (files.length === 0) {
      toast.error("Add at least one image first");
      return;
    }
    setIsCompressing(true);
    const snapshot = files.map((f) => ({ ...f, status: "compressing" }));
    setFiles(snapshot);
    const results = await Promise.all(snapshot.map(compressOne));
    setFiles(results);
    setIsCompressing(false);

    const ok = results.filter((r) => r.status === "done");
    const failed = results.length - ok.length;
    if (ok.length) {
      const original = ok.reduce((s, r) => s + r.originalSize, 0);
      const compressed = ok.reduce((s, r) => s + r.compressedSize, 0);
      const pct = original ? Math.round((1 - compressed / original) * 100) : 0;
      toast.success(`Compressed ${ok.length} image${ok.length > 1 ? "s" : ""}`, {
        description: `~${Math.max(pct, 0)}% smaller on average · ${formatBytes(original)} → ${formatBytes(compressed)}`,
      });
    }
    if (failed) toast.error(`${failed} image${failed > 1 ? "s" : ""} failed to compress`);
  };

  const downloadOne = (entry) => {
    if (!entry.compressedUrl) return;
    const a = document.createElement("a");
    a.href = entry.compressedUrl;
    a.download = entry.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadAllZip = async () => {
    if (doneFiles.length === 0) {
      toast.error("Compress images first");
      return;
    }
    setIsZipping(true);
    try {
      const zip = new JSZip();
      doneFiles.forEach((f) => zip.file(f.name, f.compressedBlob));
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compressed-images.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't build the zip file");
    } finally {
      setIsZipping(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const onInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  return (
    <div className="space-y-6" data-testid="panel-price-reducer">
      {/* Header */}
      <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-purple-50 p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30 shrink-0">
            <PackageCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-lg font-extrabold text-slate-900">Shipping Reducer · Image Compressor</div>
            <div className="text-sm text-slate-600 mt-1 max-w-xl">
              Smaller listing images can drop you into a cheaper Meesho shipping slab. Compress up to {MAX_FILES} images at once, right in your browser.
            </div>
          </div>
        </div>
        {doneFiles.length > 0 && (
          <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-2.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Avg. size reduced</div>
            <div className="font-heading text-lg font-extrabold text-slate-900 tabular-nums">{Math.max(avgSavedPct, 0)}%</div>
          </div>
        )}
      </div>

      {/* Quality control */}
      <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading text-sm font-extrabold text-slate-900">Output quality</div>
            <div className="text-xs text-slate-500 mt-0.5">Lower quality = smaller file. Applies to every image you compress below.</div>
          </div>
          <Badge className="rounded-full bg-purple-100 text-purple-700 hover:bg-purple-100 font-bold tabular-nums text-sm px-3 py-1">
            {quality}%
          </Badge>
        </div>
        <div className="mt-5 flex items-center gap-4">
          <span className="text-[11px] font-semibold text-slate-400 w-16">Smaller file</span>
          <Slider
            value={[quality]}
            onValueChange={(v) => setQuality(v[0])}
            min={10}
            max={95}
            step={5}
            data-testid="reducer-quality-slider"
            className="flex-1"
          />
          <span className="text-[11px] font-semibold text-slate-400 w-20 text-right">Higher quality</span>
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="reducer-dropzone"
        className={`rounded-3xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
          isDragging ? "border-purple-500 bg-purple-50/60" : "border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/30"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          multiple
          onChange={onInputChange}
          data-testid="reducer-file-input"
          className="hidden"
        />
        <div className="mx-auto w-14 h-14 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center mb-4">
          <UploadCloud className="w-6 h-6" />
        </div>
        <div className="font-heading text-base font-bold text-slate-900">Drag & drop images here</div>
        <div className="text-sm text-slate-500 mt-1">or click to browse · JPG, JPEG, PNG, WEBP · up to {MAX_FILES} files</div>
        <div className="text-xs text-slate-400 mt-2">{files.length} / {MAX_FILES} selected</div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden" data-testid="reducer-file-list">
          <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-heading text-sm font-extrabold text-slate-900">Selected images</div>
              <div className="text-xs text-slate-500 mt-0.5">{files.length} file{files.length > 1 ? "s" : ""} · {formatBytes(files.reduce((s, f) => s + f.originalSize, 0))} total</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={clearAll}
                data-testid="reducer-clear-all"
                className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold h-9 px-4 text-xs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear all
              </Button>
              {doneFiles.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadAllZip}
                  disabled={isZipping}
                  data-testid="reducer-download-zip"
                  className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 font-semibold h-9 px-4 text-xs"
                >
                  <FileArchive className="w-3.5 h-3.5 mr-1.5" /> {isZipping ? "Zipping…" : "Download all (.zip)"}
                </Button>
              )}
              <Button
                type="button"
                onClick={compressAll}
                disabled={isCompressing}
                data-testid="reducer-compress-all"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-9 px-4 text-xs shadow-lg shadow-emerald-600/25"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {isCompressing ? "Compressing…" : "Compress all"}
              </Button>
            </div>
          </div>

          <ul className="divide-y divide-slate-100">
            <AnimatePresence initial={false}>
              {files.map((f) => (
                <motion.li
                  key={f.id}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-6 py-3 flex items-center gap-3"
                  data-testid={`reducer-file-${f.id}`}
                >
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
                    {f.previewUrl ? (
                      <img src={f.previewUrl} alt={f.name} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-slate-400" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{f.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <span className="tabular-nums">{formatBytes(f.originalSize)}</span>
                      {f.status === "done" && (
                        <>
                          <span>→</span>
                          <span className="tabular-nums font-bold text-emerald-700">{formatBytes(f.compressedSize)}</span>
                          <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] font-bold py-0">
                            -{Math.max(Math.round((1 - f.compressedSize / f.originalSize) * 100), 0)}%
                          </Badge>
                        </>
                      )}
                      {f.status === "error" && <span className="text-red-600">{f.error}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {f.status === "compressing" && <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />}
                    {f.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    {f.status === "error" && <AlertCircle className="w-4 h-4 text-red-500" />}

                    {f.status === "done" && (
                      <button
                        onClick={() => downloadOne(f)}
                        data-testid={`reducer-download-${f.id}`}
                        className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-xs font-semibold text-purple-700 hover:bg-purple-50"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                    )}
                    <button
                      onClick={() => removeFile(f.id)}
                      data-testid={`reducer-remove-${f.id}`}
                      aria-label="Remove"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </div>
      )}
    </div>
  );
};
